import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { Toaster } from '@/components/ui/sonner';
import ProtectedRoute from '@/components/ProtectedRoute';
import AuthCallback from '@/pages/AuthCallback';
import Landing from '@/pages/Landing';
import Dashboard from '@/pages/Dashboard';
import RestaurantList from '@/pages/RestaurantList';
import RestaurantDetail from '@/pages/RestaurantDetail';
import MyReviews from '@/pages/MyReviews';
import UploadReceipt from '@/pages/UploadReceipt';
import WalletPage from '@/pages/WalletPage';
import RestaurantAuth from '@/pages/RestaurantAuth';
import RestaurantDashboard from '@/pages/RestaurantDashboard';
import ValidationsPage from '@/pages/ValidationsPage';
import ReceivedReviews from '@/pages/ReceivedReviews';
import RestaurantProfile from '@/pages/RestaurantProfile';
import AdminDashboard from '@/pages/AdminDashboard';
import ContractPage from '@/pages/ContractPage';
import UserAuth from '@/pages/UserAuth';
import SocialProjects from '@/pages/SocialProjects';
import SocialProjectDetail from '@/pages/SocialProjectDetail';
import ProfileEdit from '@/pages/ProfileEdit';
import AdminSocialProjects from '@/pages/AdminSocialProjects';
import { useAuth } from '@/contexts/AuthContext';
import { AdminMfaGate } from '@/components/AdminMfaGate';

// UI-only guard; the backend enforces permissions on every admin API.
function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.admin_permissions === undefined) return null; // waiting for /auth/me refresh
  if (!user?.admin_permissions?.includes('admin.read')) return <Navigate to="/dashboard" replace />;
  return <AdminMfaGate>{children}</AdminMfaGate>;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/user-auth" element={<UserAuth />} />
      <Route path="/restaurant-auth" element={<RestaurantAuth />} />
      <Route path="/restaurants" element={<RestaurantList />} />
      <Route path="/restaurants/:id" element={<RestaurantDetail />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/my-reviews" element={<ProtectedRoute><MyReviews /></ProtectedRoute>} />
      <Route path="/receipts/upload/:referralId" element={<ProtectedRoute><UploadReceipt /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
      <Route path="/restaurant/dashboard" element={<ProtectedRoute><RestaurantDashboard /></ProtectedRoute>} />
      <Route path="/restaurant/validations" element={<ProtectedRoute><ValidationsPage /></ProtectedRoute>} />
      <Route path="/restaurant/received-reviews" element={<ProtectedRoute><ReceivedReviews /></ProtectedRoute>} />
      <Route path="/restaurant/profile" element={<ProtectedRoute><RestaurantProfile /></ProtectedRoute>} />
      <Route path="/business/contract" element={<ProtectedRoute><ContractPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminOnly><AdminDashboard /></AdminOnly></ProtectedRoute>} />
      <Route path="/admin/social-projects" element={<ProtectedRoute><AdminOnly><AdminSocialProjects /></AdminOnly></ProtectedRoute>} />
      <Route path="/social-projects" element={<SocialProjects />} />
      <Route path="/social-projects/:id" element={<SocialProjectDetail />} />
      <Route path="/profile/edit" element={<ProtectedRoute><ProfileEdit /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster position="bottom-right" />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
